import { MemoryStorageAdapter } from '../storage/storage-adapter';
import type { StorageAdapter } from '../storage/storage-adapter';
import { DEFAULT_SETTINGS, freezeSettings, mergeSettings, SETTINGS_STORAGE_KEY, validateSettings } from './settings-schema';
import type { AppSettings, SettingsPatch } from './settings-schema';

export type SettingsListener = (settings: AppSettings) => void;
export type Unsubscribe = () => void;
export interface SettingsLogger { warn(message: string, detail?: unknown): void }

export class SettingsService {
  private current: AppSettings = DEFAULT_SETTINGS;
  private activeStorage: StorageAdapter;
  private failedOver = false;
  private readonly listeners = new Set<SettingsListener>();

  public constructor(storage: StorageAdapter, private readonly logger: SettingsLogger) {
    this.activeStorage = storage; this.load();
  }
  public get(): AppSettings { return freezeSettings(this.current); }
  public update(patch: SettingsPatch): AppSettings {
    const next = mergeSettings(this.current, patch);
    if (JSON.stringify(next) === JSON.stringify(this.current)) return this.get();
    this.current = next; this.persistCurrent(); this.notify(); return this.get();
  }
  public reset(): AppSettings {
    if (JSON.stringify(this.current) === JSON.stringify(DEFAULT_SETTINGS)) return this.get();
    this.current = DEFAULT_SETTINGS; this.persistCurrent(); this.notify(); return this.get();
  }
  public subscribe(listener: SettingsListener): Unsubscribe { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  public get usingMemoryFallback(): boolean { return this.failedOver; }

  private load(): void {
    const read = this.activeStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!read.ok) { this.logger.warn('Settings storage read failed.', read.error); this.failover(); return; }
    if (read.value === null) return;
    let parsed: unknown;
    try { parsed = JSON.parse(read.value); } catch (error) { this.repairCorrupt(error); return; }
    if (typeof parsed === 'object' && parsed !== null && typeof (parsed as { version?: unknown }).version === 'number'
      && (parsed as { version: number }).version > 1) {
      this.logger.warn('Future settings version ignored.'); this.failover(); return;
    }
    try { this.current = validateSettings(parsed); } catch (error) { this.repairCorrupt(error); }
  }
  private repairCorrupt(error: unknown): void {
    this.logger.warn('Corrupt settings replaced with defaults.', error); this.current = DEFAULT_SETTINGS;
    const repaired = this.activeStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(DEFAULT_SETTINGS));
    if (!repaired.ok) { this.logger.warn('Settings repair write failed.', repaired.error); this.failover(); }
  }
  private persistCurrent(): void {
    const result = this.activeStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this.current));
    if (!result.ok) { this.logger.warn('Settings persistence failed.', result.error); this.failover(); }
  }
  private failover(): void {
    if (this.failedOver) return;
    this.failedOver = true; this.activeStorage = new MemoryStorageAdapter();
    this.activeStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this.current));
  }
  private notify(): void { for (const listener of [...this.listeners]) listener(this.get()); }
}
